import React, {useMemo} from "react";

import {useDisclosure} from "../../../../../hooks/useDisclosure";
import {useTranslation} from "@i18n/client";
import {I18nNamespace} from "@i18n";
import {LocationFragment, useFetchSlotsInArea} from "../../../../../queries/useFetchSlotsInArea";
import {LocSlotPair} from "../../../Locations/usePairLocsWithSlots";
import {TranslateFn} from "@i18n";

const getPrompt = (
  t: TranslateFn,
  initialLocation: LocSlotPair<LocationFragment>,
  nearbyLocations: LocSlotPair<LocationFragment>[],
) => {
  const soonestTimeAtInitialLocation = initialLocation?.soonestSlot?.time;
  const maybeSoonerLocation = nearbyLocations.find(
    l =>
      l.soonestSlot?.time &&
      Number.isInteger(l.location.dist) &&
      (!soonestTimeAtInitialLocation || l.soonestSlot.time < soonestTimeAtInitialLocation),
  );

  const maybeDistToSoonerLoc = maybeSoonerLocation?.location.dist;

  return !nearbyLocations.length
    ? null
    : typeof maybeDistToSoonerLoc === "number"
    ? t("We can see you sooner {{number}} mi away", {
        ns: "web-scheduling",
        number: maybeDistToSoonerLoc,
      })
    : t("More locations near you", {
        ns: "web-scheduling",
      });
};

const Sooner = ({slotsQuery}: {slotsQuery: ReturnType<typeof useFetchSlotsInArea>}) => {
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const nearbyLocationPanel = useDisclosure();

  const nearbyLocationsPrompt = useMemo(
    () =>
      nearbyLocationPanel.isClosed
        ? getPrompt(t, slotsQuery.data.initialLocation, slotsQuery.data.nearbyLocations)
        : "",
    [
      nearbyLocationPanel.isClosed,
      slotsQuery.data.initialLocation,
      slotsQuery.data.nearbyLocations,
      t,
    ],
  );

  return nearbyLocationsPrompt ? (
    <div className="flex items-center flex-col gap-4 sm:flex-row py-1 w-full">
      <span className="font-ttr">{nearbyLocationsPrompt}</span>
    </div>
  ) : null;
};

export default React.memo(Sooner);
